import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { useTranslation } from "react-i18next";
import "flatpickr/dist/themes/material_blue.css";
import { isEmpty, map, size } from "lodash";
import DeleteModal from "../../components/Common/DeleteModal";

import {
  addListingStatus,
  getListingStatus,
  updateListingStatus,
  deleteListingStatus,
} from "../../store/actions";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from "reactstrap";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import ActionEdit from "components/buttons/actionEdit";
import ActionDelete from "components/buttons/actionDelete";
import SafeField from "helpers/SafeField";

const ListingStatus = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { listingstatuses, loading } = useSelector(
    (state) => state.ObjectReducer
  );

  const [listingstatus, setListingstatus] = useState({});
  const [isRight, setIsRight] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  // -----------------------------------
  // Init
  // -----------------------------------
  useEffect(() => {
    dispatch(getListingStatus(localStorage.getItem("userId")));
  }, [dispatch]);

  // -----------------------------------
  // Toggles
  // -----------------------------------
  const toggleRightCanvas = (row) => {
    if (row) {
      setListingstatus({
        id: row.id,
        listingStatusId: row.listingStatusId,
        listingStatusTitle: row.listingStatusTitle,
        agentId: row.agentId,
        isActive: row.isActive,
      });
      setIsEdit(true);
    } else {
      setListingstatus({});
      setIsEdit(false);
    }
    setIsRight((prev) => !prev);
  };

  const toggleDeleteModal = () => {
    setDeleteModal((prev) => !prev);
  };

  // -----------------------------------
  // CRUD
  // -----------------------------------
  const handleListingStatusSubmit = (values) => {
    dispatch(addListingStatus(values));
  };

  const onClickDelete = (row) => {
    setListingstatus(row);
    setDeleteModal(true);
  };

  const handleDeleteListingStatus = () => {
    if (listingstatus?.id) {
      dispatch(deleteListingStatus(listingstatus));
      setDeleteModal(false);
    }
  };

  const handleUpdate = (values) => {
    if (isEdit) {
      const updateData = {
        id: values.id,
        listingStatusId: values.listingStatusId,
        listingStatusTitle: values.listingStatusTitle,
        agentId: values.agentId,
        isActive: values.isActive,
      };
      dispatch(updateListingStatus(updateData));
    }
    toggleRightCanvas();
  };

  // -----------------------------------
  // Render
  // -----------------------------------
  return (
    <>
      {loading && <div className="loading">Loading…</div>}
      <ToastContainer autoClose={2000} />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteListingStatus}
        onCloseClick={toggleDeleteModal}
      />

      <div className="page-content">
       

        <Container fluid>
          <Row className="mb20">
            <Col xl="12">
              <Breadcrumbs
                title={t("General Settings")}
                breadcrumbItem={t("Listing Status")}
              />
            </Col>
          </Row>

          <Row>
            
            <Col xl="12">
              {/* ================= ADD ================= */}
              <Formik
                enableReinitialize
                initialValues={{
                  listingStatusTitle: "",
                  agentId: parseInt(localStorage.getItem("userId")),
                }}
                validationSchema={Yup.object({
                  listingStatusTitle: Yup.string().required("Required"),
                })}
                onSubmit={handleListingStatusSubmit}
              >
                {({ errors, touched, handleChange }) => (
                  <Form className="needs-validation">
                    <Card>
                      <CardBody>
                        <Row>
                          <Col sm="3">
                            <FormGroup className="mb-3">
                              <Label>{t("Title")}</Label>
                              <SafeField
                                name="listingStatusTitle"
                                onChange={handleChange}
                                className={
                                  "form-control" +
                                  (errors.listingStatusTitle &&
                                  touched.listingStatusTitle
                                    ? " is-invalid"
                                    : "")
                                }
                              />
                              <ErrorMessage
                                name="listingStatusTitle"
                                component="div"
                                className="invalid-feedback"
                              />
                            </FormGroup>
                          </Col>

                          <Col sm="2">
                            <button
                              type="submit"
                              className="btn btn-primary w-md"
                              style={{ marginTop: 31 }}
                            >
                              Add
                            </button>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>

                    {/* ================= TABLE ================= */}
                    <Card>
                      <CardBody>
                        <h5 className="card-title">
                         History
                        </h5>

                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Title</th>
                                <th>Action</th>
                              </tr>
                            </thead>

                            <tbody>
                              {map(listingstatuses, (row, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>{row.listingStatusTitle}</td>
                                  <td>
                                    <ActionEdit
                                      onClick={() =>
                                        toggleRightCanvas(row)
                                      }
                                    />
                                    <ActionDelete
                                      onClick={() => onClickDelete(row)}
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardBody>
                    </Card>
                  </Form>
                )}
              </Formik>
            </Col>
          </Row>

          {/* ================= UPDATE ================= */}
          <Offcanvas
            isOpen={isRight}
            direction="end"
            toggle={toggleRightCanvas}
          >
            <OffcanvasHeader toggle={toggleRightCanvas}>
              Update
            </OffcanvasHeader>

            <OffcanvasBody>
              <Formik
                enableReinitialize
                initialValues={{
                  id: listingstatus?.id,
                  listingStatusId: listingstatus?.listingStatusId,
                  listingStatusTitle: listingstatus?.listingStatusTitle,
                  agentId: listingstatus?.agentId,
                  isActive: listingstatus?.isActive,
                }}
                validationSchema={Yup.object({
                  listingStatusTitle: Yup.string().required("Required"),
                })}
                onSubmit={handleUpdate}
              >
                {({ errors, touched, handleChange }) => (
                  <Form className="needs-validation">
                    <Row>
                      <Col sm="12">
                        <FormGroup className="mb-3">
                          <Label>{t("Title")}</Label>
                          <SafeField
                            name="listingStatusTitle"
                            onChange={handleChange}
                            className={
                              "form-control" +
                              (errors.listingStatusTitle &&
                              touched.listingStatusTitle
                                ? " is-invalid"
                                : "")
                            }
                          />
                          <ErrorMessage
                            name="listingStatusTitle"
                            component="div"
                            className="invalid-feedback"
                          />
                        </FormGroup>
                      </Col>

                      <Col sm="12">
                        <button
                          type="submit"
                          className="btn btn-primary w-md"
                        >
                          Update
                        </button>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>
            </OffcanvasBody>
          </Offcanvas>
        </Container>
      </div>
    </>
  );
};

ListingStatus.propTypes = {
  listingstatuses: PropTypes.array,
  loading: PropTypes.any,
};

export default ListingStatus;
